"use client";
import React, { JSX, memo, useState } from "react";
import cx from "classnames";

import { TableDataType, TableHeaderDataType } from "./type";

import styles from "./styles.module.scss";

export interface TableProps {
    columns: TableHeaderDataType[];
    data: TableDataType[];
    tableClassName?: string;
    headerCellClassName?: string;
    tableDataClassName?: string;
    tableBodyClassName?: string;
    tableHeaderClassName?: string;
    tableRowClassName?: string;
    columnStyles?: { [key: string]: React.CSSProperties };
    headerCellStyles?: { [key: string]: React.CSSProperties };
    onRowSelect?: (selectedRows: TableDataType[], uuid: number) => void;
    tableHeadClassName?: string;
    handleOnClick?: (accessor: string) => void;
    dataTheme?: string;
    renderSubRows?: (row: TableDataType) => JSX.Element | null;
    dataCellInnerChild?: string;
    isSeparator?: boolean;
}

const Table = (props: TableProps) => {
    const {
        data,
        columns,
        tableClassName,
        tableDataClassName,
        tableBodyClassName,
        headerCellClassName,
        tableHeaderClassName,
        tableRowClassName,
        columnStyles,
        onRowSelect,
        tableHeadClassName,
        handleOnClick,
        dataTheme,
        renderSubRows,
        dataCellInnerChild,
        isSeparator = true,
        headerCellStyles,
    } = props;

    const [selectedRowId, setSelectedRowId] = useState<string | number | null>(
        null,
    );

    const dataWithUUID = data?.map((row, index) => ({
        ...row,
        uuid: row.uuid || row.id || index,
    }));

    return (
        <table
            data-theme={dataTheme}
            cellPadding="0"
            cellSpacing="0"
            className={cx(tableClassName, styles.tableWrapper, styles.table)}
        >
            <thead data-theme={dataTheme} className={cx(tableHeadClassName)}>
                <tr
                    data-theme={dataTheme}
                    className={cx(tableHeaderClassName, styles.tableHead)}
                >
                    {columns?.map((column) => (
                        <th
                            data-theme={dataTheme}
                            key={column.accessor}
                            className={cx(
                                headerCellClassName,
                                styles.headerCell,
                            )}
                            style={{
                                ...columnStyles?.[column.accessor],
                                ...headerCellStyles?.[column.accessor],
                            }}
                            onClick={() =>
                                handleOnClick && handleOnClick(column.accessor)
                            }
                        >
                            {column?.header}
                        </th>
                    ))}
                </tr>
            </thead>

            <tbody
                data-theme={dataTheme}
                className={cx(tableBodyClassName, styles.tbody)}
            >
                {dataWithUUID?.map((row) => (
                    <React.Fragment key={row.uuid}>
                        <tr
                            data-theme={dataTheme}
                            className={cx(tableRowClassName, styles.tableBody, {
                                [styles.selectedRow]:
                                    selectedRowId === row.uuid,
                            })}
                            onClick={() => {
                                setSelectedRowId(row.uuid);
                                onRowSelect?.(row, row.uuid);
                            }}
                        >
                            {columns?.map((column) => {
                                return (
                                    <td
                                        data-theme={dataTheme}
                                        key={column.accessor}
                                        className={cx(
                                            tableDataClassName,
                                            styles.column_data,
                                        )}
                                        style={columnStyles?.[column.accessor]}
                                    >
                                        <div className={dataCellInnerChild}>
                                            {row[column.accessor] ||
                                                (isSeparator ? "-" : "0")}
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                        {renderSubRows && (
                            <tr
                                style={{
                                    backgroundColor: "transparent",
                                    display: "flex",
                                }}
                            >
                                {renderSubRows(row)}
                            </tr>
                        )}
                    </React.Fragment>
                ))}
            </tbody>
        </table>
    );
};

export default memo(Table);
